/**
 * CaseDetailsSummary.tsx
 * 
 * Individual case details table showing institution, classification, error details
 * for each case. Displays in a dark-themed table format similar to the reference design.
 */

import { FC } from 'react';
import { ConnectionCase, PlaidLinkEvent } from '../../services/plaidConnectionMonitorService';

interface CaseDetailsSummaryProps {
  cases: ConnectionCase[];
  caseEvents: Map<number, PlaidLinkEvent[]>; // Map of case_id -> events
  memberEmail?: string;
}

const CaseDetailsSummary: FC<CaseDetailsSummaryProps> = ({
  cases,
  caseEvents,
  memberEmail
}) => {
  if (cases.length === 0) {
    return null;
  }

  // Extract error details from events for each case
  const getCaseError = (caseId: number): { error: string; errorCode: string } => {
    const events = caseEvents.get(caseId) || [];
    const errorEvent = events.find(e => e.error_code || e.error_type);
    
    if (errorEvent) {
      if (errorEvent.error_code === 'INVALID_CREDENTIALS') {
        return { error: 'Password failure', errorCode: errorEvent.error_code };
      }
      if (errorEvent.error_code === 'INSTITUTION_NO_LONGER_SUPPORTED') {
        return { error: errorEvent.error_code, errorCode: errorEvent.error_code };
      }
      return { 
        error: errorEvent.error_message_sanitized || errorEvent.error_type || 'Unknown', 
        errorCode: errorEvent.error_code || ''
      };
    }
    
    return { error: 'Unknown', errorCode: '' };
  };

  const formatClassification = (classification: string | null): string => {
    if (!classification) return 'unknown';
    return classification;
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  };

  // Get member email from first case or use prop
  const displayEmail = memberEmail || cases[0]?.member_email || 'Unknown Member';
  const displayName = cases[0]?.member_name || 'Unknown Member';

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h3 className="text-lg font-semibold text-slate-700 mb-4">
        {cases.length} Case{cases.length !== 1 ? 's' : ''} Created for {displayName}{' '}
        <span className="text-blue-600">({displayEmail})</span>:
      </h3>

      {/* Dark-themed table */}
      <div className="bg-slate-900 rounded-lg overflow-hidden">
        <table className="w-full text-white">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-3 px-4 font-semibold text-slate-300 uppercase text-xs tracking-wider">
                Case
              </th>
              <th className="text-left py-3 px-4 font-semibold text-slate-300 uppercase text-xs tracking-wider">
                Institution
              </th>
              <th className="text-left py-3 px-4 font-semibold text-slate-300 uppercase text-xs tracking-wider">
                Classification
              </th>
              <th className="text-left py-3 px-4 font-semibold text-slate-300 uppercase text-xs tracking-wider">
                Error
              </th>
              <th className="text-left py-3 px-4 font-semibold text-slate-300 uppercase text-xs tracking-wider">
                Created At
              </th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => {
              const { error, errorCode } = getCaseError(c.id);
              return (
                <tr key={c.id} className="border-b border-slate-800 hover:bg-slate-800 transition-colors">
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-slate-700 rounded text-sm font-semibold">
                      #{c.id}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium">
                    {c.institution_name || 'Unknown'}
                  </td>
                  <td className="py-3 px-4">
                    <code className="bg-slate-800 px-2 py-1 rounded text-xs font-mono text-green-400">
                      {formatClassification(c.classification)}
                    </code>
                  </td>
                  <td className="py-3 px-4">
                    {error}
                  </td>
                  <td className="py-3 px-4 text-slate-400">
                    {formatTimestamp(c.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CaseDetailsSummary;
